import {
    SofaFormControl,
    SofaFormControlDescriptionWithGtm,
    SofaFormValidator,
    SofaFormWithGtm
} from '@sofology/sofologycomponents';

import {
    ComponentTypeDescription,
    ConditionDescription,
    NumberPropertyDescription,
    PropertyDescriptionTypes,
    SofaFormCrossValidationHandlerType,
    StringPropertyDescription,
} from 'models';
import { analyticsHelper } from '../analyticsHelper';
import { errorToBasketErrorModel } from '../errorToBasketErrorModel';
import { conditionDescriptionsToFunction } from './conditionHelper';
import { createRefByComponentType } from './createRefByComponentType';
import { PropertyValueType, SofaFormType, SofaFormControlDescriptionType, DataAccessObjectType, IlpApplicationDataType, DynamicControlFilterType, SHOULD_BE_RENDERED_FUNC_NAME } from './formHelperAdditionalData';
import { getDateControlFilters, getNumberControlFilters } from './getDefaultControlFilters';

import { getControlFilters } from './getControlFilters';
import { getBooleanControlValidators, getFormCrossValidationHandler, getNumberControlValidators, getStringControlValidators } from './getControlValidators';
import { getGtmName } from './getGtmName';
import { getFlatObject } from 'helpers/dynamicFormHelpers/getFlatObject';
import { getInputTypeByComponentTypeDescription } from './getInputTypeByComponentTypeDescription';
import { createPropertiesMap, getDefaultValueByProperty } from './propertyDescriptionHelpers';
import { isBooleanProperty, isNumberProperty, isNumberableSelectProperty, isDateProperty } from './propertyTypeHelper';

export class FormGeneratorHelper {
    private dataAccessObject: DataAccessObjectType = {};
    private conditionsDescription: Record<string, (ConditionDescription | null)[]> = {};

    public createSofaForm(formName: string, properties: Record<string, PropertyDescriptionTypes>, setFormState: (updateStateFunc: any) => void, getState: () => any, globalState: IlpApplicationDataType): { form: SofaFormType; crossValidationHandler: SofaFormCrossValidationHandlerType } {
        let localDataAccessObject: DataAccessObjectType = {};

        for (let propertyKey in globalState) {
            localDataAccessObject[propertyKey] = () => globalState[propertyKey];
        }

        this.dataAccessObject = localDataAccessObject;

        let controls = this.getFormControls(properties);

        let form = new SofaFormWithGtm<Record<string, SofaFormControl<PropertyValueType>>>(
            controls,
            setFormState,
            formName,
            getState,
            formName,
            analyticsHelper.sendToGoogleMessageAboutInputChangeCompleted.bind(analyticsHelper),
            errorToBasketErrorModel
        );

        this.connectDataAccessObjectToForm(localDataAccessObject, getState, form, formName);
        this.setShouldBeRenderedFuncToControls(form);
        this.setValidateFormFunc(form);

        return {
            form,
            crossValidationHandler: this.createFormCrossValidationHandler(properties, controls, formName),
        };
    }

    public createFormCrossValidationHandler(
        properties: Record<string, PropertyDescriptionTypes>,
        formDescription: Record<string, SofaFormControlDescriptionWithGtm<any>>,
        formName: string
    ): SofaFormCrossValidationHandlerType {
        const propertiesMap = createPropertiesMap(properties);

        return getFormCrossValidationHandler(
            controlName => propertiesMap[controlName],
            propertyName => propertyName,
            formDescription,
            formName
        );
    }

    public extractFormDataToIlpObject(form: SofaFormType, properties: Record<string, PropertyDescriptionTypes>): Record<string, PropertyValueType> {
        let data: Record<string, PropertyValueType> = {};
        const propertiesMap = createPropertiesMap(properties);

        for (let controlKey in form.controls) {
            const control = form.controls[controlKey]!;
            const property = propertiesMap[control.name];
            let controlValue: PropertyValueType | Record<string, string | boolean | null> = control.value;

            if (!control[SHOULD_BE_RENDERED_FUNC_NAME]()) {
                controlValue = property.componentType === ComponentTypeDescription.Address
                    ? getFlatObject(property.properties, {})
                    : property.defaultValueForFailedCondition ?? getDefaultValueByProperty(property);
            }

            if (property.componentType === ComponentTypeDescription.Address) {
                data = { ...data, ...(controlValue as unknown as Record<string, PropertyValueType>) };
            } else {
                data = { ...data, [control.name]: controlValue as any };
            }
        }

        return data;
    }

    public async validateReadonlyControls(properties: Record<string, PropertyDescriptionTypes>, form: SofaFormType): Promise<SofaFormControl<any>[]> {
        // need to wait until AddressEditor fill internal form values
        const promiseWaiter = new Promise(resolve => {
            setTimeout(() => {
                resolve(null);
            }, 500);
        });

        await promiseWaiter;

        const propertiesMap = createPropertiesMap(properties);
        let invalidControls = [];

        for (const controlName in form.controls) {
            const control = form.controls[controlName];
            const property = propertiesMap[control.name];

            if (property.readonly && control[SHOULD_BE_RENDERED_FUNC_NAME]()) {
                const invalidControl = await control.validate();
                if (invalidControl) {
                    invalidControls.push(invalidControl);
                }
            }
        }

        return invalidControls;
    }

    private getFormControls(properties: Record<string, PropertyDescriptionTypes>): Record<string, SofaFormControlDescriptionType> {
        const controls: Record<string, SofaFormControlDescriptionType> = {};

        for (const propertyKey in properties) {
            const property = properties[propertyKey];

            if (property.componentType === ComponentTypeDescription.Address) {
                this.handleAddressControl(property, controls);
            } else {
                this.handleNonAddressControl(property, controls);
            }
        }

        this.fillControlIndexes(controls);

        return controls;
    }

    private handleAddressControl(property: PropertyDescriptionTypes, controls: Record<string, SofaFormControlDescriptionType>): void {
        const addressControl = this.createControl(property);
        controls[property.name] = addressControl;
        this.conditionsDescription[property.name] = [property.condition];
    }

    private handleNonAddressControl(property: PropertyDescriptionTypes, controls: Record<string, SofaFormControlDescriptionType>): void {
        if (property.properties) {
            this.handleSubProperties(property, controls);
        } else {
            const newControl = this.createControl(property);
            controls[property.name] = newControl;
            this.conditionsDescription[property.name] = [property.condition];
        }
    }

    private handleSubProperties(property: PropertyDescriptionTypes, controls: Record<string, SofaFormControlDescriptionType>): void {
        for (const subPropKey in property.properties) {
            const subProp = property.properties[subPropKey];
            const newControl = this.createControl(subProp);
            controls[subProp.name] = newControl;
            this.conditionsDescription[subProp.name] = [subProp.condition, property.condition];
        }
    }

    private createControl(property: PropertyDescriptionTypes): SofaFormControlDescriptionWithGtm<PropertyValueType> {
        let inputType = getInputTypeByComponentTypeDescription(property.componentType);
        let controlRef = createRefByComponentType(inputType, property);
        let controlGtmName = getGtmName(property.name);
        let value = this.getValueFromState(property);
        let filters: DynamicControlFilterType[] = getControlFilters(property);

        if (isBooleanProperty(property)) {
            let validators: SofaFormValidator<boolean>[] = getBooleanControlValidators(property, this.dataAccessObject);

            return {
                name: property.name,
                defaultValue: value,
                validators: validators,
                filters: filters,
                ref: controlRef,
                inputType: inputType,
                gtmFieldName: controlGtmName
            } as SofaFormControlDescriptionWithGtm<boolean>;
        }

        if (isNumberProperty(property) || isNumberableSelectProperty(property)) {
            let validators: SofaFormValidator<number | null>[] = getNumberControlValidators(property as NumberPropertyDescription, this.dataAccessObject);
            filters = getNumberControlFilters(property);

            return {
                name: property.name,
                defaultValue: value,
                validators: validators,
                filters: filters,
                ref: controlRef,
                inputType: inputType,
                gtmFieldName: controlGtmName
            } as SofaFormControlDescriptionWithGtm<number | null>;
        }

        let validators: SofaFormValidator<string>[] = getStringControlValidators(property as StringPropertyDescription, this.dataAccessObject);

        if (isDateProperty(property)) {
            filters = getDateControlFilters();
        }

        return {
            name: property.name,
            defaultValue: value,
            validators: validators,
            filters: filters,
            ref: controlRef,
            inputType: inputType,
            gtmFieldName: controlGtmName
        } as SofaFormControlDescriptionWithGtm<string>;
    }

    private shouldBeRendered(controlName: string): Function {
        let conditionDescriptions = this.conditionsDescription[controlName];

        return conditionDescriptionsToFunction(conditionDescriptions, this.dataAccessObject);
    }

    private connectDataAccessObjectToForm(localDataAccessObject: DataAccessObjectType, getState: () => any, form: SofaFormType, formName: string) {
        for (let controlKey in form.controls) {
            let control = form.controls[controlKey];

            localDataAccessObject[control.name] = () => getState()[formName].controls[control.name].value;
        }

        this.dataAccessObject = localDataAccessObject;
    }

    private setShouldBeRenderedFuncToControls(form: SofaFormType) {
        for (let controlKey in form.controls) {
            let control = form.controls[controlKey];

            control[SHOULD_BE_RENDERED_FUNC_NAME] = this.shouldBeRendered(controlKey);
        }
    }

    private getValueFromState(property: PropertyDescriptionTypes): (PropertyValueType) | Record<string, PropertyValueType> {
        if (property.componentType === ComponentTypeDescription.Address) {
            return this.getAddrssValueFromState(property);
        }

        return this.dataAccessObject[property.name]();
    }

    private getAddrssValueFromState(addressProperty: PropertyDescriptionTypes): (PropertyValueType) | Record<string, PropertyValueType> {
        let result: Record<string, PropertyValueType> = {};

        for (let propertyKey in addressProperty.properties) {
            let addressPropertyName = addressProperty.properties[propertyKey].name;
            let addressPropertyValue = this.dataAccessObject[addressPropertyName]();

            result = { ...result, [addressPropertyName]: addressPropertyValue };
        }

        return result;
    }

    private setValidateFormFunc(form: SofaFormWithGtm<Record<string, SofaFormControl<PropertyValueType>>>): void {
        form.validate = async () => {
            let validationPromises = [];
            for (const controlName in form.controls) {
                let control = (form.controls[controlName] as unknown) as SofaFormControl<any>;

                if (control[SHOULD_BE_RENDERED_FUNC_NAME]()) {
                    validationPromises.push(control.validate());
                }
            }

            return Promise.all(validationPromises)
                .then(validationResults => {
                    return validationResults
                        .filter(vr => vr !== null)
                        .sort(vr => vr!.index) as SofaFormControl<any>[];
                });
        };
    }

    private fillControlIndexes(controls: Record<string, SofaFormControlDescriptionType>): void {
        const CONTROL_INDEX_OFFSET = 100;
        let controlIndex = CONTROL_INDEX_OFFSET;

        for (const controlName in controls) {
            controls[controlName].index = controlIndex;

            controlIndex += CONTROL_INDEX_OFFSET;
        }
    }
}

export const formGeneratorHelper = new FormGeneratorHelper();